import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { ethers } from "ethers";

import {
  PriceType,
  TimeUnit,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { MintDetails } from "components/sale/PriceTypeInput";
import { now, nowPlus1Month } from "utils/date";
import { getPriceDropIntervalTime } from "utils/duthAuctions";
import { MintVector } from "~/features/tokens/sections/ManageToken/MintVector/MultipleMintVector";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

export type SaleForm = {
  isPaused: boolean;
  saleType: SaleType;
  saleName: string;
  saleSize: number;
  collectorMessage?: string;
  gateId: string | null;
  price?: number;
  currency: string;
  saleDateFrom: Date;
  saleDateTo: Date | null;
  scheduledPrices?: string[] | null;
  priceDropInterval?:
    | {
        unit: TimeUnit;
        value: number;
      }[]
    | null;
  isDirectMint: boolean;
  limitPerWallet: number;
  shouldLimitNumberOfMintPerWallet: boolean;
  paymentRecipient: string;
  paymentRecipientEOA?: string;
  mintDetails: MintDetails;
};

export enum SaleType {
  PUBLIC = "Public",
  PRIVATE = "Private",
}

const useSaleForm = ({
  mintVector,
  isFixedPriceCollection,
  collection,
  enableGatelessPrivateSales,
}: {
  mintVector?: MintVector | null;
  isFixedPriceCollection?: boolean;
  collection: GetManageTokenCollectionQuery["getCollection"];
  enableGatelessPrivateSales: boolean;
}) => {
  const { walletAddress } = useAuth();

  const firstPaymentRecipient = collection?.mintVectors[0]?.paymentRecipient;

  const initialValues = {
    isPaused: false,
    saleType: SaleType.PRIVATE,
    saleName: "",
    currency: ethers.constants.AddressZero,
    saleSize: 0,
    gateId: null,
    isDirectMint: false,
    collectorMessage: "",
    limitPerWallet: 1,
    saleDateFrom: now(),
    saleDateTo: nowPlus1Month(),
    shouldLimitNumberOfMintPerWallet: false,
    paymentRecipient: firstPaymentRecipient || walletAddress || "",
    paymentRecipientEOA: firstPaymentRecipient || walletAddress || "",
    price: 0,
    mintDetails: {
      distributionType: TokenDistributionType.Mint,
      priceType: isFixedPriceCollection
        ? PriceType.Fixed
        : PriceType.DutchAuction,
      isFreePrice: true,
    },
  };

  const mapMintVectorToSaleForm = (mintVector: MintVector) => ({
    isPaused: mintVector.paused ?? false,
    gateId: mintVector.gateId,
    saleType: mintVector.isPublic ? SaleType.PUBLIC : SaleType.PRIVATE,
    saleName: mintVector.name || "",
    saleSize: mintVector.maxPerVector || 0,
    collectorMessage: mintVector.collectorMessage || "",
    price: mintVector.price ? +mintVector.price : undefined,
    currency: mintVector.currency,
    saleDateFrom: new Date(mintVector.start),
    saleDateTo: mintVector.end ? new Date(mintVector?.end) : null,
    scheduledPrices: mintVector.scheduledPrices,
    priceDropInterval: mintVector.priceDropInterval,
    isDirectMint: !!mintVector.isDirectMint,
    limitPerWallet: mintVector.maxPerUser,
    shouldLimitNumberOfMintPerWallet: mintVector.maxPerUser !== 0,
    paymentRecipient: mintVector.paymentRecipient,
    paymentRecipientEOA: mintVector.paymentRecipient,
    mintDetails: {
      distributionType: TokenDistributionType.Mint,
      priceType: mintVector.priceType || PriceType.Fixed,
      isFreePrice: mintVector.price ? +mintVector.price === 0 : false,
    },
  });

  const validate = (
    values: SaleForm,
    collection: GetManageTokenCollectionQuery["getCollection"]
  ) => ({
    saleName: !values.saleName
      ? "Name is required"
      : values.saleName.length < 3
      ? "Name should have more than 3 characters"
      : null,
    saleSize:
      collection.size && !values.saleSize
        ? "Size is required"
        : values.saleSize && values.saleSize <= 0
        ? "Size should be positive"
        : collection.size &&
          values.saleSize &&
          values.saleSize > collection.size
        ? `Size should be less than ${collection.size}`
        : null,
    gateId:
      values.saleType === SaleType.PRIVATE &&
      !values.gateId &&
      !enableGatelessPrivateSales
        ? "Gate is required"
        : null,
    price:
      values.mintDetails.priceType === PriceType.Fixed &&
      !values.price &&
      values.price !== 0
        ? "Price is required"
        : null,
    currency:
      !values.currency && values.mintDetails.priceType === PriceType.Fixed
        ? "Currency is required"
        : null,
    paymentRecipientEOA:
      !values.paymentRecipient ||
      !ethers.utils.isAddress(values.paymentRecipient)
        ? "Valid Payment recipient is required"
        : null,
    scheduledPrices:
      values.mintDetails.priceType === PriceType.DutchAuction &&
      !values.scheduledPrices?.length
        ? "Price is required"
        : values.mintDetails.priceType === PriceType.DutchAuction &&
          values.scheduledPrices!.length < 2
        ? "You must add more than one price for a Dutch Auction"
        : null,
    priceDropInterval:
      values.mintDetails.priceType === PriceType.DutchAuction &&
      !values.priceDropInterval?.length
        ? "Drop interval is required"
        : values.mintDetails.priceType === PriceType.DutchAuction &&
          values.priceDropInterval?.every((period) => period.value === 0)
        ? "Drop interval must be greater 0"
        : values.mintDetails.priceType === PriceType.DutchAuction &&
          values.saleDateTo &&
          values.scheduledPrices?.length &&
          values.saleDateTo.getTime() -
            (values.saleDateFrom.getTime() +
              getPriceDropIntervalTime(values.priceDropInterval) *
                (values.scheduledPrices.length - 1)) <
            0
        ? "Drop interval cannot exceed the end time"
        : null,
  });

  return {
    validate,
    initialValues: mintVector
      ? mapMintVectorToSaleForm(mintVector)
      : initialValues,
  };
};

export default useSaleForm;
